import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { loadBootConfig } from '@/boot/config'
import { initApiClient } from '@/data/apiClient'
import { loadBootConfiguration } from '@/data/bootConfig'
import { createAppQueryClient } from '@/data/query'
import { AppRoot } from '@/AppRoot'
import { RateLimitBanner } from '@/RateLimitBanner'
import '@/styles/main.css'

/**
 * LLD-IDN-180 — three boot states: loading (the config fetch is in flight), error (it failed —
 * shown per §33.6 rather than a blank screen), ready (the API client is initialised and the
 * router can mount). No route renders before `ready`; LLD-SPA-004 is why there is no default
 * base URL to render against in the meantime.
 */
type BootState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready' }

const queryClient = createAppQueryClient()

// `AppRoot` hands off between the auth layer, org resolution and the shell on its own; the
// router's job is only to give `react-router-dom` a history to mount `ShellLayer`'s nested
// `<Routes>` against, so one catch-all path is correct here, not a gap.
const router = createBrowserRouter([{ path: '*', element: <AppRoot /> }])

function App() {
  const [boot, setBoot] = useState<BootState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    loadBootConfig()
      .then((config) => {
        if (cancelled) return
        initApiClient(`${config.apiBaseUrl}/v1`)
        return loadBootConfiguration()
      })
      .then(() => {
        if (cancelled) return
        setBoot({ status: 'ready' })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setBoot({ status: 'error', message: err instanceof Error ? err.message : String(err) })
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (boot.status === 'loading') {
    return (
      <div role="status" aria-live="polite">
        Loading…
      </div>
    )
  }

  if (boot.status === 'error') {
    return (
      <div role="alert">
        Could not load configuration. {boot.message}
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RateLimitBanner />
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
